/**
 * Palette de commandes (PB-125) : ce qu'on peut atteindre au clavier — pages, tâches, fiches, actions — et le filtre
 * appliqué à la saisie. Logique pure, testée sans navigateur.
 */
export type Command = { id: string; group: "Aller à" | "Actions" | "Tâches" | "Fiches"; label: string; hint?: string; to: string };

export const PAGES: { section: string; label: string; shortcut?: string }[] = [
  { section: "today", label: "Aujourd'hui", shortcut: "a" },
  { section: "board", label: "Tâches", shortcut: "t" },
  { section: "live", label: "En direct", shortcut: "l" },
  { section: "knowledge", label: "Connaissance", shortcut: "k" },
  { section: "review", label: "À revoir", shortcut: "r" },
  { section: "health", label: "Santé et usage", shortcut: "h" },
  { section: "members", label: "Membres", shortcut: "m" },
  { section: "settings", label: "Réglages", shortcut: "s" },
];

export function buildCommands(
  project: string,
  work: { key: string; title: string; status: string }[],
  items: { key: string; title: string; type: string }[],
  org = false,
): Command[] {
  const base = `/p/${project}`;
  const pages = PAGES.filter((p) => !org || ["knowledge", "review", "health", "members", "settings"].includes(p.section));
  return [
    ...pages.map((p) => ({ id: `page:${p.section}`, group: "Aller à" as const, label: p.label, hint: p.shortcut ? `g ${p.shortcut}` : undefined, to: `${base}/${p.section}` })),
    ...(org ? [] : [{ id: "action:new-task", group: "Actions" as const, label: "Nouvelle tâche", to: `${base}/board?new=1` }]),
    { id: "action:new-item", group: "Actions" as const, label: "Nouvelle fiche de connaissance", to: `${base}/knowledge?new=1` },
    ...(org ? [] : [{ id: "action:ready", group: "Actions" as const, label: "Tâches prêtes à prendre", to: `${base}/board?view=ready` }]),
    ...work
      .filter((w) => w.status !== "cancelled")
      .map((w) => ({ id: `work:${w.key}`, group: "Tâches" as const, label: `${w.key} ${w.title}`, hint: w.status, to: `${base}/board?task=${encodeURIComponent(w.key)}` })),
    ...items.map((i) => ({ id: `item:${i.key}`, group: "Fiches" as const, label: `${i.key} ${i.title}`, hint: i.type, to: `${base}/knowledge/item/${encodeURIComponent(i.key)}` })),
  ];
}

const fold = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

/**
 * Commandes qui correspondent à la saisie, les meilleures d'abord : clé exacte, puis début du libellé, puis tous les mots
 * présents (accents et casse ignorés). Sans saisie : les pages et les actions.
 */
export function filterCommands(commands: Command[], query: string, limit = 12): Command[] {
  const q = fold(query.trim());
  if (!q) return commands.filter((c) => c.group === "Aller à" || c.group === "Actions").slice(0, limit);
  const words = q.split(/\s+/);
  return commands
    .map((c) => {
      const label = fold(c.label);
      const key = label.split(" ")[0];
      const score = key === q ? 0 : label.startsWith(q) ? 1 : words.every((w) => label.includes(w)) ? 2 : -1;
      return { c, score };
    })
    .filter((x) => x.score >= 0)
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((x) => x.c);
}
